import { useCallback, useEffect, useMemo, useState } from "react";
import { z } from "zod";

const STORAGE_KEYS = {
  enabled: "actualfood_tablet_kiosk_enabled",
  pin: "actualfood_tablet_kiosk_pin",
} as const;

const kioskPinSchema = z
  .string()
  .trim()
  .regex(/^\d{4,8}$/, "O PIN deve ter 4 a 8 dígitos");

function safeGetItem(key: string) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function safeRemoveItem(key: string) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function useTabletKiosk() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const raw = safeGetItem(STORAGE_KEYS.enabled);
    setEnabled(raw === "true");
  }, []);

  const hasPin = useMemo(() => {
    const pin = safeGetItem(STORAGE_KEYS.pin);
    return !!pin;
  }, [enabled]);

  const setPin = useCallback((pin: string) => {
    const parsed = kioskPinSchema.safeParse(pin);
    if (!parsed.success) return { ok: false as const, message: parsed.error.issues[0]?.message || "PIN inválido" };
    safeSetItem(STORAGE_KEYS.pin, parsed.data);
    return { ok: true as const };
  }, []);

  const enable = useCallback((pin: string) => {
    const pinResult = setPin(pin);
    if (!pinResult.ok) return pinResult;
    safeSetItem(STORAGE_KEYS.enabled, "true");
    setEnabled(true);
    return { ok: true as const };
  }, [setPin]);

  const disable = useCallback(() => {
    safeSetItem(STORAGE_KEYS.enabled, "false");
    setEnabled(false);
  }, []);

  const clearPin = useCallback(() => {
    safeRemoveItem(STORAGE_KEYS.pin);
  }, []);

  const verify = useCallback((pinAttempt: string) => {
    const parsed = kioskPinSchema.safeParse(pinAttempt);
    if (!parsed.success) return { ok: false as const, message: parsed.error.issues[0]?.message || "PIN inválido" };
    const stored = safeGetItem(STORAGE_KEYS.pin);
    if (!stored) return { ok: false as const, message: "PIN não configurado" };
    return stored === parsed.data
      ? { ok: true as const }
      : { ok: false as const, message: "PIN incorreto" };
  }, []);

  return {
    enabled,
    hasPin,
    enable,
    disable,
    setPin,
    clearPin,
    verify,
  };
}
